import { deepMerge } from '../../../utils/object';

export const CHART_COLORS = ['#3A92E5', '#C496ED', '#5CB85C', '#FFAA20', '#E64D5F', '#00D0B2', '#EB3D89', '#FFD797'];

const STYLE_COLORS = {
    text: '#334155',
    textLight: '#64748B',
    gridLine: '#E2E8F0',
    border: '#CBD5E1',
    background: '#FFFFFF',
};

// Applied only to heatmaps. A colorAxis on any other chart type would render an
// unwanted color-legend bar, so it is merged in conditionally (see buildWidgetOptions).
export const DEFAULT_HEATMAP_COLOR_AXIS = {
    min: 0,
    minColor: '#FFFFFF',
    maxColor: '#3A92E5',
};

export const DEFAULT_HIGHCHARTS_CONFIG = {
    colors: CHART_COLORS,
    chart: {
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        reflow: true,
    },
    credits: { enabled: false },
    title: {
        style: {
            color: STYLE_COLORS.text,
            fontWeight: '500',
        },
    },
    subtitle: { style: { color: STYLE_COLORS.textLight } },
    legend: {
        itemStyle: { color: STYLE_COLORS.text },
        itemHoverStyle: { color: '#3A92E5' },
    },
    tooltip: {
        backgroundColor: STYLE_COLORS.background,
        borderColor: STYLE_COLORS.border,
        style: { color: STYLE_COLORS.text },
    },
    plotOptions: {
        series: { animation: { duration: 1000 } },
        pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
                enabled: true,
                format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
            showInLegend: true,
        },
        scatter: {
            tooltip: { pointFormat: '<b>{point.name}</b><br/>X: {point.x}<br/>Y: {point.y}' },
        },
        bubble: {
            dataLabels: {
                enabled: true,
                format: '{point.name}',
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
        },
        treemap: {
            dataLabels: {
                enabled: true,
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
        },
        heatmap: {
            // Thin cell borders keep the grid legible even when values are pale.
            borderWidth: 1,
            borderColor: STYLE_COLORS.gridLine,
            dataLabels: {
                enabled: true,
                // 'contrast' auto-picks black/white per cell so labels stay readable
                // on both light and saturated cells.
                color: 'contrast',
                style: { textOutline: 'none', fontWeight: '500' },
            },
        },
    },
    responsive: {
        rules: [
            {
                condition: {
                    maxWidth: 500,
                },
                chartOptions: {
                    legend: {
                        enabled: false,
                    },
                },
            },
        ],
    },
};

// Return a copy of a single colorAxis object with any hard-coded `max` removed,
// so the heatmap scale auto-fits its data. Non-objects pass through untouched.
const stripColorAxisMax = colorAxis => {
    if (!colorAxis || typeof colorAxis !== 'object' || Array.isArray(colorAxis)) return colorAxis;
    const next = { ...colorAxis };
    delete next.max;
    return next;
};

// Build the heatmap colorAxis from whatever the agent supplied:
// - an array of axes → keep the array shape, strip each axis's `max`
// - an object (or nothing) → brand-blue default underneath, agent keys on top,
//   `max` stripped. Agents often emit a round `max` (e.g. 10000) far above the
//   largest cell, which washes the map out to near-white.
// Always returns fresh objects, so the caller's config is never mutated.
const buildHeatmapColorAxis = provided => {
    if (Array.isArray(provided)) return provided.map(stripColorAxisMax);
    const merged = { ...DEFAULT_HEATMAP_COLOR_AXIS, ...(provided && typeof provided === 'object' ? provided : {}) };
    return stripColorAxisMax(merged);
};

// Merge the sanitized chart config over the shared defaults.
// Heatmaps additionally get a brand-blue, auto-scaling colorAxis (agent-overridable);
// other chart types never receive one so they don't render a stray color legend.
export const buildWidgetOptions = config => {
    if (!config) return null;

    const options = deepMerge(DEFAULT_HIGHCHARTS_CONFIG, config);

    if (config.chart && config.chart.type === 'heatmap') {
        options.colorAxis = buildHeatmapColorAxis(config.colorAxis);
    }

    return options;
};
